import React, { useState, useEffect, Suspense } from "react";
import { BrowserRouter, Outlet, Route, Routes, redirect } from "react-router-dom";
import Header from "./routes/Header";
import Footer from "./routes/Footer";
import Container from "./components/Container";
import NotificationsProvider from "./components/NotificationsProvider";
import MaintenanceWrapper from "./components/MaintenanceWrapper";
import LoadingSpinner from "./components/ergo-elements/components/LoadingSpinner";
import { useKeycloakContext } from "./contexts/AuthContext";
import PrivateRoute from "./helpers/PrivateRoute";
import { useNavigate, useLocation } from "react-router-dom";

const TOKEN_EXPIRY_THRESHOLD_SECONDS = 120; // Token considered 'about to expire'
const REFRESH_INTERVAL_MS = 120000; // Run refresh check every 2 minutes

// LAZY LOADING ROUTES
const AdmissionForm = React.lazy(() => import("./routes/AdmissionForm"));
const Admissions = React.lazy(() => import("./routes/Admissions"));
const Cookies = React.lazy(() => import("./routes/Cookies"));
const Medicard = React.lazy(() => import("./routes/Medicard"));
const AdmissionConfirmation = React.lazy(() =>
  import("./routes/AdmissionConfirmation")
);
const Help = React.lazy(() => import("./routes/Help/Help"));
const BlankDocuments = React.lazy(() => import("./routes/BlankDocuments"));
const Search = React.lazy(() => import("./routes/Search/"));

const AppLayout = ({ handleOnRef }) => {
  const { initialized, ...keycloak } = useKeycloakContext();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    // Update the document title using the browser API
    if (keycloak.authenticated && initialized && !!keycloak.tokenParsed) {
      setLoading(false);
      window.provider_id = keycloak.tokenParsed.provider_id;

      if (!window.lang) {
        window.lang = keycloak.tokenParsed.locale ? keycloak.tokenParsed.locale.toLowerCase() : "en";
        let oldPath = location.pathname;
        let segments = oldPath.split('/');
        let redirect = false;
        if (segments.length > 1) {
          let urlLang = segments[1].toLowerCase();
          if (urlLang !== window.lang) {
            segments[1] = window.lang;
            redirect = true;
          }
        }

        if (redirect) {
          let path = segments.join('/');
          navigate(path, { replace: true, state: location.state });
        }
      }
    }
  }, [keycloak.authenticated, initialized, keycloak.tokenParsed, location.pathname, location.state, navigate]);

  useEffect(() => {
    const refreshToken = async () => {
      if (initialized) {
        try {
          await keycloak.updateToken(TOKEN_EXPIRY_THRESHOLD_SECONDS); // Refresh token if it will expire in the next 110 seconds
        } catch (error) {
          console.warn("Failed to refresh access token, session may be expiring soon.", error);
        }
      }
    };
    const id = setInterval(refreshToken, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [initialized, keycloak]);

  if (loading) return <LoadingSpinner show={loading} />;

  return (
    <MaintenanceWrapper>
      <div style={{ minHeight: "calc(100vh - 145px)" }}>
        <NotificationsProvider>
          <Header onRef={handleOnRef} />
          <Container>
            <Outlet />
          </Container>
        </NotificationsProvider>
      </div>
      <div style={{ marginTop: "80px", backgroundColor: "#f0efee" }}>
        <Footer />
      </div>
    </MaintenanceWrapper>
  );
};

export default class Router extends React.Component {
  constructor(props) {
    super(props);
    this.onPartnerIdChanged = this.onPartnerIdChanged.bind(this);
  }

  render() {
    return (
      <BrowserRouter basename="/hospiadmission">
        <Suspense fallback={<LoadingSpinner show={true} />}>
          <Routes>
            <Route
              path=":lang"
              element={<AppLayout handleOnRef={(ref) => (this.child = ref)} />}
            >
              <Route element={<PrivateRoute />}>
                <Route path="admissions" element={<Admissions />} />
                <Route
                  path="admissions/:admissionNumber"
                  loader={({ params }) => {
                    const ok = /^\d{10}$/.test(params.admissionNumber ?? "");
                    return ok
                      ? null
                      : redirect(
                        `/hospiadmission/${params.lang}/admissions/${params.admissionNumber}`
                      );
                  }}
                  element={<AdmissionForm />}
                />
                <Route path="admissions/:medicard" element={<Admissions />} />
                <Route
                  path="admissions-patient"
                  element={<Admissions patientView={true} />}
                />
                <Route path="admission-confirmation/:admissionNumber?/*" element={<AdmissionConfirmation />} />
                <Route path="blank-forms" element={<BlankDocuments />} />
                <Route path="cookies" element={<Cookies />} />
                <Route path="medicards/:medicard" element={<Medicard />} />
                <Route
                  path="medicards/:medicard/admission-form"
                  element={<AdmissionForm />}
                />
                <Route path="search" element={<Search />} />
                <Route path="help" element={<Help />} />
              </Route>
              <Route
                path="*"
                element={<p>You are not authorized to access this site.</p>}
              />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  onPartnerIdChanged(partnerId) {
    this.child.load(partnerId);
  }
}
